"use client";

import BrandCard from "./BrandCard";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons } from "../assets/assets";
import Loader from "@/components/Loader";

interface ClientItem {
  clientLogo: string;
  tagLine: string;
  smallOverview: string;
}

interface EnterPriseProps {
  ctaText?: string;
  showPagination?: boolean;
  enterpriseItems?: ClientItem[];
  categoryData?: any;
  loading?: boolean;
  error?: string | null;
}

import { useState } from "react";

export default function EnterPrise({
  loading = false,
  error = null,
  categoryData,
  enterpriseItems = [],
  ctaText = "Let's Build Something Amazing",
  showPagination = true,
}: EnterPriseProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 8;

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    const maxPage = Math.ceil(enterpriseItems.length / itemsPerPage) - 1;
    if (currentPage < maxPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const defaultSectionTitle = (
    <>
      Engineered for
      <br />
      <span className="text-[#225BD4]">Enterprises</span>
    </>
  );

  const displayedItems = enterpriseItems.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage,
  );

  const hasMultiplePages = enterpriseItems.length > itemsPerPage;
  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center w-full h-[60vh] py-20">
          <Loader size={80} />
        </div>
      ) : error ? (
        <div className="text-center py-32">
          <p className="text-xl md:text-2xl text-red-600 font-medium">
            {error}
          </p>
        </div>
      ) : enterpriseItems.length > 0 ? (
        <section className="w-full bg-white py-[60px] md:py-[100px] lg:py-[150px] px-4 md:px-8 lg:px-[100px]">
          <div className="max-w-[5000px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-[30px] md:gap-[40px] lg:gap-[30px] justify-between">
              {/* Left Side - Text Content */}
              <div className="w-full lg:w-[845px] flex flex-col gap-[20px] md:gap-[25px] lg:gap-[30px]">
                <div className="flex flex-col gap-[12px] md:gap-[15px]">
                  {categoryData?.title && (
                    <p className="text-[14px] md:text-[16px] font-medium text-[#00040b] uppercase">
                      {categoryData.title}
                    </p>
                  )}
                  <h2 className="text-[32px] md:text-[48px] lg:text-[length:var(--section-title)] font-medium clash-font text-[#00040b] leading-[120%]">
                    {defaultSectionTitle}
                  </h2>
                </div>

                {categoryData?.descriptions && (
                  <p className="text-[16px] md:text-[20px] lg:text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] font-normal text-[#00040b]/70 leading-[140%] whitespace-pre-line">
                    {categoryData.descriptions}
                  </p>
                )}

                {ctaText && (
                  <PrimaryBtn
                    text={ctaText}
                    iconSrc={Icons?.chevron}
                    dark={false}
                    href={"/contact-us"}
                  />
                )}
              </div>

              {/* Right Side - Brand Grid */}
              <div className="w-full lg:w-[845px] flex flex-col items-end gap-[20px] md:gap-[25px] lg:gap-[30px]">
                {/* Mobile Pagination */}
                {showPagination && hasMultiplePages && (
                  <div className="flex items-center gap-[8px] w-full justify-end lg:hidden">
                    <button
                      onClick={handlePrevious}
                      aria-label="Previous slide"
                      disabled={currentPage === 0}
                      className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px] rounded-full border border-[#ecf0f6] bg-white flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M12.5 15L7.5 10L12.5 5"
                          stroke="#00040b"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={handleNext}
                      aria-label="Next slide"
                      disabled={
                        currentPage >=
                        Math.ceil(enterpriseItems.length / itemsPerPage) - 1
                      }
                      className="w-[32px] h-[32px] sm:w-[40px] sm:h-[40px] rounded-full bg-[#225bd4] flex items-center justify-center hover:bg-[#1a4aa8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M7.5 5L12.5 10L7.5 15"
                          stroke="#ffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Brand Cards Grid */}
                <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-0">
                  {displayedItems?.map((brand, index: number) => (
                    <BrandCard key={`${brand}-${index}`} brand={brand} />
                  ))}
                </div>

                {/* Desktop Pagination */}
                {showPagination && hasMultiplePages && (
                  <div className="hidden lg:flex items-center gap-[15px]">
                    <button
                      onClick={handlePrevious}
                      aria-label="Previous slide"
                      disabled={currentPage === 0}
                      className="w-[40px] h-[40px] rounded-full border border-[#ecf0f6] bg-white flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M12.5 15L7.5 10L12.5 5"
                          stroke="#00040b"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={handleNext}
                      aria-label="Next slide"
                      disabled={
                        currentPage >=
                        Math.ceil(enterpriseItems.length / itemsPerPage) - 1
                      }
                      className="w-[40px] h-[40px] rounded-full bg-[#225bd4] flex items-center justify-center hover:bg-[#1a4aa8] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M7.5 5L12.5 10L7.5 15"
                          stroke="#ffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className="text-center py-32">
          <p className="text-xl md:text-2xl text-gray-600">
            No trusted clients to display at the moment.
          </p>
        </div>
      )}
    </>
  );
}
